<script setup lang="ts">
  import type {
    Stakeholder,
    ChangeStakeholderVisitAddressRequestPayload
  } from '@gat-ship/types'
  import { testIds } from '@gat-ship/types'
  import {
    FormField,
    FormSubmit,
    InputGroupTextarea,
    RadioCardGroup,
    RadioCardGroupItem,
    Button,
    Modal,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalHeaderDescription,
    ModalHeaderTitle,
    ModalSection,
    useForm,
    z
  } from '@gat-ship/vue-components'
  import { ref } from 'vue'
  import { IconCopy, IconEdit } from '@gat-ship/icons'
  import { FieldGroup } from '@gat-ship/vue-components'
  import { text } from '@gat-ship/stakeholders'
  import { useStakeholderContext } from '../../composables/useStakeholder'
  import type { ShowStakeholderUpdateVisitAddressParams } from '../providers/stakeholder-modals'
  import StakeholderBtnDeleteVisitAddress from '../buttons/StakeholderBtnDeleteVisitAddress.vue'

  // Edit-modal setup (open model + injected service + staged stakeholder +
  // title/description refs) is the same idiomatic boilerplate in every modal.
  // fallow-ignore-next-line code-duplication
  const isOpen = defineModel<boolean>('open', { default: false })
  const service = useStakeholderContext()

  const _stakeholder = ref<Stakeholder>()
  const modalTitle = ref('')
  const modalDescription = ref('')

  const schema = z
    .object({
      stakeholderId: z.string(),
      visitAddressType: z.enum([
        'same-as-post-address',
        'custom-visit-address'
      ]),
      visitAddress: z.string().optional()
    })
    .superRefine((data, ctx) => {
      if (
        data.visitAddressType === 'custom-visit-address' &&
        (!data.visitAddress || data.visitAddress.length === 0)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['visitAddress'],
          message: 'Visit address is required'
        })
      }
    })
  type FormValues = z.infer<typeof schema>

  const form = useForm({
    defaultValues: {
      stakeholderId: '',
      visitAddressType: 'custom-visit-address',
      visitAddress: ''
    } as FormValues,
    validators: {
      onChange: schema
    },
    onSubmit: async ({ value }) => {
      const isSameAsPostal = value.visitAddressType === 'same-as-post-address'

      // Send `null` (not `undefined`) to clear the visit address on the
      // backend — an omitted key leaves the field unchanged, so toggling to
      // "same as post" with `undefined` would leave a stale custom address
      // in the DB, contradicting the flag.
      const payload: ChangeStakeholderVisitAddressRequestPayload = {
        visitAddressSameAsPostal: isSameAsPostal,
        visitAddress: isSameAsPostal ? null : value.visitAddress || null
      }
      await service?.commands.changeVisitAddress(value.stakeholderId, payload)
      isOpen.value = false
    }
  })

  function showModal({ stakeholder }: ShowStakeholderUpdateVisitAddressParams) {
    _stakeholder.value = stakeholder
    modalTitle.value = text.groups.visitAddress(
      stakeholder.stakeholderType
    ).modals.changeVisitAddress.title
    modalDescription.value = text.groups
      .visitAddress(stakeholder.stakeholderType)
      .modals.changeVisitAddress.description(stakeholder.name ?? '')
    form.reset({
      stakeholderId: stakeholder.id,
      visitAddressType: stakeholder.visitAddressSameAsPostal
        ? 'same-as-post-address'
        : 'custom-visit-address',
      visitAddress: stakeholder.visitAddress ?? ''
    })
    isOpen.value = true
  }

  defineExpose({ showModal })
</script>

<template>
  <modal v-model:open="isOpen">
    <ModalHeader>
      <ModalHeaderTitle>
        {{ modalTitle }}
      </ModalHeaderTitle>
      <ModalHeaderDescription v-if="modalDescription">
        {{ modalDescription }}
      </ModalHeaderDescription>
    </ModalHeader>
    <ModalContent>
      <ModalSection>
        <FormField :form="form" name="visitAddressType" :input-group="false">
          <template #default="{ field: typeField }">
            <RadioCardGroup
              :model-value="typeField.state.value"
              @update:model-value="typeField.handleChange"
            >
              <RadioCardGroupItem
                value="same-as-post-address"
                label="Same as Post Address"
                description="The visit address is the same as the post address"
                :icon="IconCopy"
                :data-testid="
                  testIds.stakeholders.forms.visitAddress.sameAsPostalRadio
                "
              />
              <RadioCardGroupItem
                value="custom-visit-address"
                label="Custom Visit Address"
                description="Manually set the visit address"
                :icon="IconEdit"
                :data-testid="
                  testIds.stakeholders.forms.visitAddress
                    .customVisitAddressRadio
                "
              >
                <template #footer="{ selected }">
                  <FieldGroup v-if="selected">
                    <FormField
                      :form="form"
                      name="visitAddress"
                      label="Visit Address"
                      required
                    >
                      <template #default="{ field }">
                        <InputGroupTextarea
                          :id="field.name"
                          :model-value="field.state.value ?? ''"
                          autofocus
                          :data-testid="
                            testIds.stakeholders.forms.visitAddress.addressInput
                          "
                          @update:model-value="field.handleChange"
                          @blur="field.handleBlur"
                        />
                      </template>
                    </FormField>
                  </FieldGroup>
                </template>
              </RadioCardGroupItem>
            </RadioCardGroup>
          </template>
        </FormField>
      </ModalSection>

      <ModalSection v-if="_stakeholder">
        <StakeholderBtnDeleteVisitAddress
          class="w-fit"
          :stakeholder="_stakeholder"
          @saving="isOpen = false"
        />
      </ModalSection>
    </ModalContent>
    <ModalFooter>
      <FormSubmit :form="form" label="Save Changes" />
      <Button variant="ghost" @click="isOpen = false"> Cancel </Button>
    </ModalFooter>
  </modal>
</template>
